import {
  ButtonHTMLAttributes,
  InputHTMLAttributes,
  KeyboardEvent as ReactKeyboardEvent,
  ReactNode,
  RefObject,
  SelectHTMLAttributes,
  useEffect,
  useId,
  useRef,
} from 'react'

type AdminButtonVariant = 'primary' | 'secondary' | 'ghost' | 'danger'
type AdminStatusTone = 'neutral' | 'success' | 'warning' | 'danger' | 'info' | 'pink'

// Orders opts in; nested confirmations share one saved list position.
let modalScrollLock: { count: number; overflow: string; x: number; y: number } | null = null

function lockModalScroll() {
  const lock = modalScrollLock ?? {
    count: 0, overflow: document.body.style.overflow, x: window.scrollX, y: window.scrollY,
  }
  modalScrollLock = lock
  lock.count += 1
  document.body.style.overflow = 'hidden'
  return () => {
    lock.count -= 1
    if (lock.count) return
    document.body.style.overflow = lock.overflow
    window.scrollTo({ left: lock.x, top: lock.y, behavior: 'instant' })
    modalScrollLock = null
  }
}

type AdminButtonProps = ButtonHTMLAttributes<HTMLButtonElement> & {
  variant?: AdminButtonVariant
  compact?: boolean
}

function focusableElements(container: HTMLElement | null) {
  if (!container) return []
  return Array.from(container.querySelectorAll<HTMLElement>(
    'a[href], button:not([disabled]), input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])',
  )).filter((element) => !element.hasAttribute('hidden') && element.offsetParent !== null)
}

export function AdminButton({ variant = 'primary', compact = false, className = '', ...props }: AdminButtonProps) {
  return (
    <button
      {...props}
      className={`admin-ui-button admin-ui-button--${variant} ${compact ? 'is-compact' : ''} ${className}`.trim()}
    />
  )
}

export function AdminCard({
  children,
  className = '',
  padding = 'normal',
}: {
  children: ReactNode
  className?: string
  padding?: 'none' | 'normal' | 'large'
}) {
  return <section className={`admin-ui-card admin-ui-card--${padding} ${className}`.trim()}>{children}</section>
}

export function AdminCardHeader({
  title,
  description,
  action,
}: {
  title: string
  description?: string
  action?: ReactNode
}) {
  return (
    <div className="admin-ui-card-header">
      <div>
        <h2>{title}</h2>
        {description && <p>{description}</p>}
      </div>
      {action && <div className="admin-ui-card-header__action">{action}</div>}
    </div>
  )
}

export function AdminStatusPill({ children, tone = 'neutral' }: { children: ReactNode; tone?: AdminStatusTone }) {
  return <span className={`admin-ui-status admin-ui-status--${tone}`}>{children}</span>
}

export function AdminField({
  label,
  hint,
  error,
  required,
  children,
}: {
  label: string
  hint?: string
  error?: string
  required?: boolean
  children: ReactNode
}) {
  return (
    <label className={`admin-ui-field ${error ? 'has-error' : ''}`}>
      <span className="admin-ui-field__label">
        {label}
        {required && <span aria-hidden="true"> *</span>}
      </span>
      {children}
      {error ? <span className="admin-ui-field__error">{error}</span> : hint ? <span className="admin-ui-field__hint">{hint}</span> : null}
    </label>
  )
}

export function AdminInput({ className = '', ...props }: InputHTMLAttributes<HTMLInputElement>) {
  return <input {...props} className={`admin-ui-input ${className}`.trim()} />
}

export function AdminSelect({ className = '', children, ...props }: SelectHTMLAttributes<HTMLSelectElement>) {
  return <select {...props} className={`admin-ui-select ${className}`.trim()}>{children}</select>
}

export function AdminFilterBar({ children, actions }: { children: ReactNode; actions?: ReactNode }) {
  return (
    <div className="admin-ui-filter-bar">
      <div className="admin-ui-filter-bar__fields">{children}</div>
      {actions && <div className="admin-ui-filter-bar__actions">{actions}</div>}
    </div>
  )
}

type AdminTab = {
  id: string
  label: string
  badge?: string | number
}

export function AdminTabs({
  tabs,
  activeId,
  onChange,
  label = 'Section tabs',
  idBase = 'admin-tabs',
}: {
  tabs: AdminTab[]
  activeId: string
  onChange: (id: string) => void
  label?: string
  idBase?: string
}) {
  const refs = useRef<Array<HTMLButtonElement | null>>([])
  const activeIndex = Math.max(0, tabs.findIndex((tab) => tab.id === activeId))

  function moveFocus(nextIndex: number) {
    if (!tabs.length) return
    const wrapped = (nextIndex + tabs.length) % tabs.length
    onChange(tabs[wrapped].id)
    window.requestAnimationFrame(() => refs.current[wrapped]?.focus())
  }

  function onTabKeyDown(event: ReactKeyboardEvent<HTMLButtonElement>, index: number) {
    if (event.key === 'ArrowRight') { event.preventDefault(); moveFocus(index + 1) }
    else if (event.key === 'ArrowLeft') { event.preventDefault(); moveFocus(index - 1) }
    else if (event.key === 'Home') { event.preventDefault(); moveFocus(0) }
    else if (event.key === 'End') { event.preventDefault(); moveFocus(tabs.length - 1) }
  }

  return (
    <div className="admin-ui-tabs" role="tablist" aria-label={label}>
      {tabs.map((tab, index) => {
        const active = tab.id === activeId
        return (
          <button
            key={tab.id}
            ref={(element) => { refs.current[index] = element }}
            id={`${idBase}-tab-${tab.id}`}
            className={`admin-ui-tab ${active ? 'is-active' : ''}`}
            type="button"
            role="tab"
            aria-selected={active}
            aria-controls={`${idBase}-panel-${tab.id}`}
            tabIndex={active ? 0 : -1}
            onKeyDown={(event) => onTabKeyDown(event, index)}
            onClick={() => onChange(tab.id)}
          >
            <span>{tab.label}</span>
            {tab.badge !== undefined && <span className="admin-ui-tab__badge">{tab.badge}</span>}
          </button>
        )
      })}
      <span className="admin-sr-only" aria-live="polite">Active tab {tabs[activeIndex]?.label}</span>
    </div>
  )
}

export function AdminTabPanel({
  idBase = 'admin-tabs',
  tabId,
  active,
  children,
}: {
  idBase?: string
  tabId: string
  active: boolean
  children: ReactNode
}) {
  return (
    <section
      id={`${idBase}-panel-${tabId}`}
      role="tabpanel"
      aria-labelledby={`${idBase}-tab-${tabId}`}
      hidden={!active}
      tabIndex={0}
      className="admin-ui-tab-panel"
    >
      {children}
    </section>
  )
}

type AdminTableColumn<T> = {
  key: string
  label: string
  width?: string
  align?: 'left' | 'center' | 'right'
  render: (row: T) => ReactNode
}

export function AdminDataTable<T>({
  columns,
  rows,
  rowKey,
  caption,
}: {
  columns: AdminTableColumn<T>[]
  rows: T[]
  rowKey: (row: T) => string
  caption?: string
}) {
  return (
    <div className="admin-ui-table-wrap">
      <table className="admin-ui-table">
        {caption && <caption className="admin-sr-only">{caption}</caption>}
        <thead>
          <tr>
            {columns.map((column) => (
              <th
                key={column.key}
                scope="col"
                style={column.width ? { width: column.width } : undefined}
                className={column.align ? `is-${column.align}` : undefined}
              >
                {column.label}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {rows.map((row) => (
            <tr key={rowKey(row)}>
              {columns.map((column) => (
                <td key={column.key} className={column.align ? `is-${column.align}` : undefined} data-label={column.label}>
                  {column.render(row)}
                </td>
              ))}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  )
}

type PaginationItem = number | string

function compactPagination(page: number, totalPages: number): PaginationItem[] {
  const total = Math.max(1, Math.floor(totalPages))
  const current = Math.min(total, Math.max(1, Math.floor(page)))
  if (total <= 7) return Array.from({ length: total }, (_, index) => index + 1)

  const candidates = new Set<number>([1, total, current - 1, current, current + 1])
  if (current <= 4) [2, 3, 4, 5].forEach((value) => candidates.add(value))
  if (current >= total - 3) [total - 4, total - 3, total - 2, total - 1].forEach((value) => candidates.add(value))
  const pages = Array.from(candidates).filter((value) => value >= 1 && value <= total).sort((a, b) => a - b)
  const items: PaginationItem[] = []

  pages.forEach((value, index) => {
    const previous = pages[index - 1]
    if (index > 0 && value - previous > 1) items.push(`ellipsis-${previous}-${value}`)
    items.push(value)
  })
  return items
}

export function AdminPagination({
  page,
  totalPages,
  onPageChange,
}: {
  page: number
  totalPages: number
  onPageChange: (page: number) => void
}) {
  const total = Math.max(1, Math.floor(totalPages))
  const current = Math.min(total, Math.max(1, Math.floor(page)))
  const items = compactPagination(current, total)

  return (
    <nav className="admin-ui-pagination" aria-label="Pagination">
      <AdminButton variant="secondary" compact type="button" disabled={current <= 1} onClick={() => onPageChange(current - 1)}>
        Previous
      </AdminButton>
      <div className="admin-ui-pagination__pages">
        {items.map((item) => typeof item === 'number' ? (
          <button
            key={item}
            className={`admin-ui-pagination__page ${item === current ? 'is-active' : ''}`}
            type="button"
            aria-current={item === current ? 'page' : undefined}
            aria-label={`Page ${item}`}
            onClick={() => onPageChange(item)}
          >
            {item}
          </button>
        ) : (
          <span key={item} className="admin-ui-pagination__ellipsis" aria-hidden="true">…</span>
        ))}
      </div>
      <AdminButton variant="secondary" compact type="button" disabled={current >= total} onClick={() => onPageChange(current + 1)}>
        Next
      </AdminButton>
    </nav>
  )
}

export function AdminModal({
  open,
  title,
  description,
  children,
  footer,
  onClose,
  closeDisabled = false,
  closeDisabledReason = 'Please wait for the update to finish before closing.',
  fitVisualViewport = false,
  returnFocusRef,
}: {
  open: boolean
  title: string
  description?: string
  children?: ReactNode
  footer?: ReactNode
  onClose: () => void
  closeDisabled?: boolean
  closeDisabledReason?: string
  fitVisualViewport?: boolean
  returnFocusRef?: RefObject<HTMLElement | null>
}) {
  const layerRef = useRef<HTMLDivElement>(null)
  const dialogRef = useRef<HTMLElement>(null)
  const previousFocusRef = useRef<HTMLElement | null>(null)
  const onCloseRef = useRef(onClose)
  const closeDisabledRef = useRef(closeDisabled)
  const titleId = useId()
  const descriptionId = useId()
  const closeStatusId = useId()

  onCloseRef.current = onClose
  closeDisabledRef.current = closeDisabled

  function isTopmost() {
    const dialogs = document.querySelectorAll('.admin-ui-modal')
    return dialogs[dialogs.length - 1] === dialogRef.current
  }

  function requestClose() {
    if (isTopmost() && !closeDisabledRef.current) onCloseRef.current()
  }

  useEffect(() => {
    if (!open || !fitVisualViewport) return
    const layer = layerRef.current
    if (!layer) return
    const viewport = window.visualViewport
    const update = () => {
      const width = viewport?.width ?? window.innerWidth
      const height = viewport?.height ?? window.innerHeight
      Object.assign(layer.style, {
        left: `${viewport?.offsetLeft ?? 0}px`, top: `${viewport?.offsetTop ?? 0}px`,
        width: `${width}px`, height: `${height}px`, right: 'auto', bottom: 'auto',
      })
      layer.style.setProperty('--admin-modal-viewport-height', `${height}px`)
      layer.dataset.compactViewport = String(width < 300 || height < 360)
    }
    update()
    viewport?.addEventListener('resize', update)
    viewport?.addEventListener('scroll', update)
    window.addEventListener('resize', update)
    const unlock = lockModalScroll()
    return () => {
      viewport?.removeEventListener('resize', update)
      viewport?.removeEventListener('scroll', update)
      window.removeEventListener('resize', update)
      unlock()
    }
  }, [open, fitVisualViewport])

  useEffect(() => {
    if (!open) return
    previousFocusRef.current = returnFocusRef?.current ?? (document.activeElement instanceof HTMLElement ? document.activeElement : null)
    const frame = window.requestAnimationFrame(() => {
      const first = focusableElements(dialogRef.current)[0]
      ;(first ?? dialogRef.current)?.focus({ preventScroll: fitVisualViewport })
    })

    const onKeyDown = (event: KeyboardEvent) => {
      if (!isTopmost()) return
      if (event.key === 'Escape') {
        event.preventDefault()
        if (!closeDisabledRef.current) onCloseRef.current()
        return
      }
      if (event.key !== 'Tab') return
      const focusable = focusableElements(dialogRef.current)
      if (!focusable.length) {
        event.preventDefault()
        dialogRef.current?.focus()
        return
      }
      const first = focusable[0]
      const last = focusable[focusable.length - 1]
      if (!dialogRef.current?.contains(document.activeElement)) {
        event.preventDefault(); (event.shiftKey ? last : first).focus()
      } else if (event.shiftKey && document.activeElement === first) {
        event.preventDefault(); last.focus()
      } else if (!event.shiftKey && document.activeElement === last) {
        event.preventDefault(); first.focus()
      }
    }

    document.addEventListener('keydown', onKeyDown)
    return () => {
      window.cancelAnimationFrame(frame)
      document.removeEventListener('keydown', onKeyDown)
      window.requestAnimationFrame(() => previousFocusRef.current?.focus({ preventScroll: fitVisualViewport }))
    }
  }, [open])

  if (!open) return null
  const content = <>
    {children && <div className="admin-ui-modal__body">{children}</div>}
    {footer && <div className="admin-ui-modal__footer">{footer}</div>}
  </>
  return (
    <div ref={layerRef} className="admin-ui-modal-layer" role="presentation">
      <div className="admin-ui-modal-scrim" aria-hidden="true" onMouseDown={requestClose} />
      <section
        ref={dialogRef}
        className="admin-ui-modal"
        role="dialog"
        aria-modal="true"
        aria-labelledby={titleId}
        aria-describedby={[description ? descriptionId : '', closeDisabled ? closeStatusId : ''].filter(Boolean).join(' ') || undefined}
        tabIndex={-1}
      >
        <button className="admin-ui-modal__close" type="button" onClick={requestClose} disabled={closeDisabled} aria-label="Close dialog" aria-describedby={closeDisabled ? closeStatusId : undefined}>×</button>
        <div className="admin-ui-modal__header">
          <h2 id={titleId}>{title}</h2>
          {description && <p id={descriptionId}>{description}</p>}
          <span id={closeStatusId} className="admin-sr-only" role="status">{closeDisabled ? closeDisabledReason : ''}</span>
        </div>
        {fitVisualViewport ? <div className="admin-ui-modal__content">{content}</div> : content}
      </section>
    </div>
  )
}

export function AdminConfirmDialog({
  open,
  title,
  description,
  confirmLabel = 'Confirm',
  cancelLabel = 'Cancel',
  dangerous = false,
  onConfirm,
  onCancel,
  closeDisabled = false,
  closeDisabledReason,
  fitVisualViewport = false,
}: {
  open: boolean
  title: string
  description: string
  confirmLabel?: string
  cancelLabel?: string
  dangerous?: boolean
  onConfirm: () => void
  onCancel: () => void
  closeDisabled?: boolean
  closeDisabledReason?: string
  fitVisualViewport?: boolean
}) {
  return (
    <AdminModal
      open={open}
      title={title}
      description={description}
      onClose={onCancel}
      closeDisabled={closeDisabled}
      closeDisabledReason={closeDisabledReason}
      fitVisualViewport={fitVisualViewport}
      footer={(
        <>
          <AdminButton variant="secondary" type="button" disabled={closeDisabled} onClick={onCancel}>{cancelLabel}</AdminButton>
          <AdminButton variant={dangerous ? 'danger' : 'primary'} type="button" disabled={closeDisabled} onClick={onConfirm}>{confirmLabel}</AdminButton>
        </>
      )}
    />
  )
}

type AdminToast = {
  id: string
  title: string
  message?: string
  tone?: AdminStatusTone
}

export function AdminToastRegion({ toasts, onDismiss }: { toasts: AdminToast[]; onDismiss: (id: string) => void }) {
  return (
    <div className="admin-ui-toasts" aria-live="polite" aria-relevant="additions removals">
      {toasts.map((toast) => (
        <div key={toast.id} className={`admin-ui-toast admin-ui-toast--${toast.tone ?? 'neutral'}`}>
          <div>
            <strong>{toast.title}</strong>
            {toast.message && <span>{toast.message}</span>}
          </div>
          <button type="button" aria-label="Dismiss notification" onClick={() => onDismiss(toast.id)}>×</button>
        </div>
      ))}
    </div>
  )
}

export function AdminStatePanel({
  state,
  title,
  description,
  action,
}: {
  state: 'loading' | 'empty' | 'error'
  title: string
  description: string
  action?: ReactNode
}) {
  return (
    <div className={`admin-ui-state admin-ui-state--${state}`} role={state === 'error' ? 'alert' : 'status'}>
      <div className="admin-ui-state__icon" aria-hidden="true">
        {state === 'loading' ? <span className="admin-ui-spinner" /> : state === 'empty' ? '○' : '!'}
      </div>
      <strong>{title}</strong>
      <p>{description}</p>
      {action && <div className="admin-ui-state__action">{action}</div>}
    </div>
  )
}

export function AdminSkeleton({ lines = 4 }: { lines?: number }) {
  return (
    <div className="admin-ui-skeleton" aria-hidden="true">
      {Array.from({ length: lines }, (_, index) => <span key={index} style={{ width: `${92 - index * 9}%` }} />)}
    </div>
  )
}

export type { AdminStatusTone, AdminTableColumn, AdminToast }
